/**
 * Header behaviour (sections/header.liquid).
 * <bag-count>: keeps Bag [n] live. On cart:added and cart:updated it writes detail.cart.item_count
 * (re-reading /cart.js when the event carries no cart) and ticks the number over: the new count
 * rises in on an add, drops in on a removal. Reduced motion swaps the number without the tick.
 * <menu-drawer>: MENU opens the drawer as a modal dialog, so the page behind is inert (focus stays
 * in the drawer) and Esc closes it natively. A link, the close button or a tap on the backdrop
 * closes it too; on close aria-expanded drops back to false and focus returns to MENU without
 * scrolling the page (preventScroll, so iOS does not jump).
 * <nav-disclosure>: a desktop menu link with children. A mouse hovering opens the panel and leaving
 * closes it; a click (or Enter on the button) opens it and keeps it open until a second click,
 * Escape (focus back on the button), tabbing out or a press elsewhere. No library.
 */
import { CART_ADDED, CART_UPDATED, defineElement, prefersReducedMotion, rootUrl } from '@wispaire/global';

const Base = globalThis.HTMLElement ?? class {};

export class BagCount extends Base {
  connectedCallback() {
    this.count = this.querySelector('[data-bag-count]');
    this.onCart = (event) => this.update(event.detail?.cart);
    document.addEventListener(CART_ADDED, this.onCart);
    document.addEventListener(CART_UPDATED, this.onCart);
  }

  disconnectedCallback() {
    document.removeEventListener(CART_ADDED, this.onCart);
    document.removeEventListener(CART_UPDATED, this.onCart);
  }

  async update(cart) {
    cart ??= await fetch(`${rootUrl()}cart.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .catch(() => null);
    if (!cart || !this.count) return;
    const previous = Number(this.count.textContent) || 0;
    const next = cart.item_count;
    this.count.textContent = String(next);
    if (prefersReducedMotion()) return;
    const from = next >= previous ? '0.9em' : '-0.9em';
    this.count.animate?.(
      [
        { transform: `translateY(${from})`, opacity: 0 },
        { transform: 'translateY(0)', opacity: 1 },
      ],
      { duration: 420, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' },
    );
  }
}

export class MenuDrawer extends Base {
  connectedCallback() {
    this.button = this.querySelector('[aria-controls="MenuDrawer"]');
    this.dialog = this.querySelector('dialog');
    if (!this.button || !this.dialog) return;

    this.button.addEventListener('click', () => {
      this.dialog.showModal();
      this.button.setAttribute('aria-expanded', 'true');
    });
    // The body fills the dialog, so a click whose target is the dialog itself landed on the backdrop.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog || event.target.closest?.('a, [data-menu-close]')) this.dialog.close();
    });
    this.dialog.addEventListener('close', () => {
      this.button.setAttribute('aria-expanded', 'false');
      this.button.focus({ preventScroll: true });
    });
  }
}

export class NavDisclosure extends Base {
  connectedCallback() {
    this.button = this.querySelector('button[aria-controls]');
    this.panel = this.querySelector('[data-nav-panel]');
    if (!this.button || !this.panel) return;
    // Opened by a click (or Enter): stays open when the pointer leaves.
    this.pinned = false;

    this.button.addEventListener('click', () => {
      if (this.pinned) this.toggle(false);
      else {
        this.pinned = true;
        this.toggle(true);
      }
    });
    // Touch fires pointerenter before its click; only a mouse opens on hover.
    this.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') this.toggle(true);
    });
    this.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'mouse' && !this.pinned) this.toggle(false);
    });
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || this.panel.hidden) return;
      event.preventDefault();
      this.toggle(false);
      this.button.focus();
    });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.toggle(false);
    });
    this.onOutside = (event) => {
      if (!this.contains(event.target)) this.toggle(false);
    };
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this.onOutside);
  }

  toggle(open) {
    if (!open) this.pinned = false;
    if (open === !this.panel.hidden) return;
    this.panel.hidden = !open;
    this.button.setAttribute('aria-expanded', String(open));
    if (open) document.addEventListener('pointerdown', this.onOutside);
    else document.removeEventListener('pointerdown', this.onOutside);
  }
}

defineElement('bag-count', BagCount);
defineElement('menu-drawer', MenuDrawer);
defineElement('nav-disclosure', NavDisclosure);
